import React, { useCallback, useEffect, useState } from 'react';
import { Empty, Skeleton, Table, Tag, Tooltip, Typography, message } from 'antd';
import { aiAdminApi, SubsystemId } from '../../../services/ai-admin-api';
import { useAuth } from '../../../hooks/useAuth';

const { Text } = Typography;

interface Props {
    subsystem: SubsystemId;
}

interface ActivityEvent {
    id: number;
    eventType: string;
    createdAt: string;
    userId: number | null;
    userEmail: string | null;
    entityType: string | null;
    entityId: number | null;
    metadata: Record<string, unknown> | null;
}

const EVENT_COLOR: Record<string, string> = {
    AI_SUBSYSTEM_VERSION_DRAFT_CREATED: 'orange',
    AI_SUBSYSTEM_VERSION_DRAFT_UPDATED: 'orange',
    AI_SUBSYSTEM_VERSION_DRAFT_DISCARDED: 'default',
    AI_SUBSYSTEM_VERSION_PUBLISHED: 'green',
    AI_SUBSYSTEM_VERSION_ROLLED_BACK: 'red',
    AI_SANDBOX_RUN_STARTED: 'blue',
    AI_SANDBOX_RUN_COMPLETED: 'green',
    AI_SANDBOX_RUN_ERROR: 'red',
    AI_SANDBOX_SCENARIO_CREATED: 'cyan',
    AI_SANDBOX_SCENARIO_UPDATED: 'cyan',
    AI_SANDBOX_SCENARIO_DELETED: 'default',
    AI_SANDBOX_DAILY_THRESHOLD_EXCEEDED: 'red',
    AI_CONFIDENTIAL_PLACEHOLDER_REJECTED: 'red',
    AI_CONFIDENTIAL_PLACEHOLDER_RESOLVE_DENIED: 'red',
    AI_SUBSYSTEM_CAPTURED_IN_SNAPSHOT: 'purple',
    AI_SUBSYSTEM_RESTORED_FROM_SNAPSHOT: 'magenta',
};

function humanLabel(eventType: string): string {
    return eventType
        .replace(/^AI_/, '')
        .toLowerCase()
        .replace(/_/g, ' ');
}

/**
 * Per-subsystem activity feed (Phase 6). Reads /admin/ai/subsystems/:id/activity
 * which filters event_logs by metadata.subsystem JSON path. Covers the full
 * lifecycle: draft created/updated/discarded/published/rolled-back, sandbox
 * run lifecycle, scenario CRUD, threshold-exceeded alerts, and confidentiality
 * rejections.
 */
export const ActivityTab: React.FC<Props> = ({ subsystem }) => {
    const { user } = useAuth();
    const jwt = user?.jwtToken ?? '';
    const [events, setEvents] = useState<ActivityEvent[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    const load = useCallback(async () => {
        if (!jwt) return;
        setIsLoading(true);
        try {
            setEvents(await aiAdminApi.listActivity(jwt, subsystem, 200));
        } catch (err: any) {
            message.error(err.message || 'Failed to load activity');
        } finally {
            setIsLoading(false);
        }
    }, [jwt, subsystem]);

    useEffect(() => {
        load();
    }, [load]);

    if (isLoading && events.length === 0) return <Skeleton active />;
    if (!isLoading && events.length === 0) {
        return <Empty description="No activity recorded for this subsystem yet." />;
    }

    return (
        <Table<ActivityEvent>
            size="small"
            rowKey="id"
            pagination={{ pageSize: 25 }}
            dataSource={events}
            columns={[
                {
                    title: 'When',
                    dataIndex: 'createdAt',
                    width: 180,
                    render: (v: string) => new Date(v).toLocaleString(),
                },
                {
                    title: 'Event',
                    dataIndex: 'eventType',
                    width: 280,
                    render: (v: string) => (
                        <Tag
                            color={EVENT_COLOR[v] ?? 'default'}
                            style={{
                                whiteSpace: 'normal',
                                lineHeight: 1.4,
                                marginRight: 0,
                            }}
                        >
                            {humanLabel(v)}
                        </Tag>
                    ),
                },
                {
                    title: 'By',
                    dataIndex: 'userEmail',
                    width: 200,
                    render: (v: string | null, row) =>
                        v ?? (
                            <Text type="secondary">
                                {row.userId ? `#${row.userId}` : '—'}
                            </Text>
                        ),
                },
                {
                    title: 'Detail',
                    dataIndex: 'metadata',
                    render: (m: Record<string, unknown> | null) => {
                        if (!m) return <Text type="secondary">—</Text>;
                        const summary = Object.entries(m)
                            .filter(
                                ([k]) =>
                                    k !== 'subsystem' && k !== 'recipients',
                            )
                            .slice(0, 4)
                            .map(([k, v]) => {
                                const value =
                                    typeof v === 'string'
                                        ? v.length > 60
                                            ? `${v.slice(0, 60)}…`
                                            : v
                                        : JSON.stringify(v);
                                return `${k}: ${value}`;
                            })
                            .join(', ');
                        return summary ? (
                            <Tooltip title={JSON.stringify(m, null, 2)}>
                                <Text ellipsis>{summary}</Text>
                            </Tooltip>
                        ) : (
                            <Text type="secondary">—</Text>
                        );
                    },
                    ellipsis: true,
                },
            ]}
        />
    );
};
